import { Lock } from '@openx/components-core/lib/Lock/Lock';
import { NameCell } from '@openx/components-core/lib/NameCell/NameCell';
import { SelectableTable } from '@openx/components-table/SelectableTable/SelectableTable';
import { Table, TableName, type TableProps } from '@openx/components-table/Table';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext';
import type { AccountsMap } from '@openx/types/account';
import type { PackageListItem, PackagesListFiltersValue } from '@openx/types/package';
import type { PackageUsedBy } from '@openx/types/package/PackageHookTypes';
import type { ToggleSelect } from '@openx/types/selectableTable';
import type { AllTableCriteria, ChangeCriteriaHandler, CriteriaDimension } from '@openx/types/tableCriteria';
import type { TableColumn } from '@openx/types/tableHelpers';
import { getFormattedAmountValue } from '@openx/utils/lib/getFormattedAmountValue';
import { priceFormat } from '@openx/utils/lib/priceFormat';
import { sortMenuButtonItems } from '@openx/utils/lib/tableHelpers';
import { noop } from '@openx/utils/noop';
import { type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PackageRowActionsType } from '../../types';
import { getEstimateRangeText } from '../../utils/getEstimateRangeText';
import { getMarketRequestsRangeText } from '../../utils/getMarketRequestsRangeText';
import { UsedByCell } from './UsedByCell';

const PackagesSelectableTable = SelectableTable<
  PackageListItem,
  PackagesListFiltersValue,
  TableProps<PackageListItem, PackagesListFiltersValue>
>(Table);

export type PackagesTableProps = {
  actions: PackageRowActionsType;
  criteria: AllTableCriteria<PackagesListFiltersValue>;
  onCriteriaChange: ChangeCriteriaHandler<CriteriaDimension, PackagesListFiltersValue>;
  packagesAccounts: AccountsMap;
  packagesList: PackageListItem[];
  hideActions?: boolean;
  hideToggleSelect?: boolean;
  hideUsedByColumn?: boolean;
  hideOrganizationColumn?: boolean;
  loading?: boolean;
  selectedData?: PackageListItem[];
  toggleSelect?: ToggleSelect<PackageListItem>;
  showCuratedBadge?: boolean;
  isCuratedList?: boolean;
  hidePrimaryRowAction?: boolean;
  usedBy?: {
    data: PackageUsedBy[];
    loading: boolean;
  };
};

export const PackagesTable = ({
  actions,
  criteria,
  onCriteriaChange,
  packagesAccounts,
  packagesList,
  loading = false,
  hideActions,
  hideToggleSelect,
  hideUsedByColumn = false,
  hideOrganizationColumn = true,
  selectedData = [],
  toggleSelect = noop,
  showCuratedBadge = false,
  isCuratedList,
  hidePrimaryRowAction = false,
  usedBy = {
    data: [],
    loading: false,
  },
}: PackagesTableProps): JSX.Element => {
  const { t } = useTranslation();
  const { useCurrentInstanceState } = useTargetingContext();
  const { isBefInstance } = useCurrentInstanceState();

  const PckgTable = hideToggleSelect ? Table : PackagesSelectableTable;

  const rowActions = useMemo(() => {
    return hideActions ? undefined : sortMenuButtonItems(actions, [actions.CREATE_DEAL]);
  }, [actions, hideActions]);

  const columnDefs: TableColumn<PackageListItem>[] = useMemo(() => {
    const baseColumns: TableColumn<PackageListItem>[] = [
      {
        disabled: true,
        key: 'name',
        render: pckg => (
          <NameCell
            id={pckg.id}
            name={pckg.name}
            pathTo={`/inventory/packages/${pckg.uid}/details${isCuratedList ? '?is_curated=true' : ''}`}
            searchPhrase={criteria.filters?.phrase}
            PreTitleIconComponent={isBefInstance && pckg.locked_internal ? <Lock marginRight gray /> : undefined}
            badge={showCuratedBadge && pckg.is_curated ? t('curated') : undefined}
          />
        ),
        title: t('Name'),
        width: '18%',
      },
      {
        key: 'account',
        render: pckg => packagesAccounts[pckg.account_uid]?.name || pckg.account_uid,
        title: t('Organization'),
        width: '15%',
        hide: hideOrganizationColumn,
      },
      {
        key: 'ad_placement',
        render: pckg => pckg.ad_placement || null,
        title: t('Format'),
        width: '12%',
      },
      {
        key: 'distribution_channel',
        render: pckg => pckg.distribution_channel || null,
        title: t('Channel'),
        width: '12%',
      },
      {
        key: 'device_type',
        render: pckg => pckg.device_type || null,
        title: t('Device'),
        width: '13%',
      },
    ];

    const curatedPackagesColumns: TableColumn<PackageListItem>[] = [
      {
        key: 'estimated_cpm',
        render: pckg => getEstimateRangeText(pckg.estimates?.cpm, v => `$${getFormattedAmountValue(v)}`),
        title: t('CPM'),
        width: '10%',
      },
      {
        key: 'estimated_reach',
        render: pckg => getMarketRequestsRangeText(pckg.estimates?.reach),
        title: t('Unique Reach'),
        width: '10%',
        hide: true,
      },
      {
        key: 'estimated_market_requests',
        render: pckg => getMarketRequestsRangeText(pckg.estimates?.market_requests),
        title: t('Market Requests'),
        width: '10%',
      },
    ];

    const standardColumns: TableColumn<PackageListItem>[] = [
      {
        key: 'rate_card_cpm',
        render: pckg => priceFormat(pckg.rate_card_cpm, packagesAccounts[pckg.account_uid]?.currency),
        title: t('Rate Card CPM'),
        width: '15%',
      },
      {
        key: 'discounted_cpm',
        render: pckg => priceFormat(pckg.discounted_cpm, packagesAccounts[pckg.account_uid]?.currency),
        title: t('Discounted CPM'),
        width: '15%',
      },
    ];

    const usedByColumn: TableColumn<PackageListItem>[] = !hideUsedByColumn
      ? [
          {
            key: 'used_by',
            render: pckg => (
              <UsedByCell usedByLoading={usedBy.loading} usedBy={usedBy.data.find(({ uid }) => uid === pckg.uid)} />
            ),
            title: t('Used by'),
            width: '15%',
          },
        ]
      : [];

    return [...baseColumns, ...(isCuratedList ? curatedPackagesColumns : standardColumns), ...usedByColumn];
  }, [
    t,
    criteria.filters?.phrase,
    isBefInstance,
    showCuratedBadge,
    packagesAccounts,
    usedBy,
    hideUsedByColumn,
    hideOrganizationColumn,
    isCuratedList,
  ]);

  return (
    <PckgTable
      columns={columnDefs}
      criteria={criteria}
      data={packagesList}
      loading={loading}
      name={TableName.PACKAGES}
      onCriteriaChange={onCriteriaChange}
      primaryRowAction={actions.CREATE_DEAL}
      hidePrimaryRowAction={hidePrimaryRowAction}
      rowActions={rowActions}
      selectedItems={selectedData}
      toggleSelect={toggleSelect}
      loadingOnInit
    />
  );
};
